/**
 * VideoPlayer Component
 * Version: 3.2.0
 * 
 * Changelog v3.2.0:
 * - AJOUT : Synchronisation bidirectionnelle avec initialMarker
 * - Le VideoPlayer met à jour ses markers quand initialMarker change de l'extérieur
 * - Permet la modification des markers depuis les champs timecode/duration du parent
 * - Utilise useEffect pour détecter les changements d'initialMarker sans créer de boucle
 * 
 * Changelog v3.1.0:
 * - MODIFICATION : Les contrôles et le curseur disparaissent après 5 secondes d'inactivité
 * - COMPORTEMENT : Indépendant de l'état de lecture (pause ou play)
 * - Les contrôles et le curseur réapparaissent au moindre mouvement de souris
 * - Le timer de 5 secondes se réinitialise à chaque mouvement
 */

import React, { useState, useRef, useEffect } from 'react';
import Hls, { ErrorData } from 'hls.js';
import { Play, Pause, Volume2, VolumeX, Maximize, SkipForward, SkipBack, Settings, Film, X } from 'lucide-react';
import styles from './VideoPlayer.module.scss';

interface VideoPlayerProps {
  url?: string;
  poster?: string;
  onReady?: () => void;
  onStart?: () => void;
  onError?: (error: ErrorData) => void;
  onProgress?: (progress: number) => void;
  onEnd?: () => void;
  onPause?: () => void;
  onSeek?: (currentTime: number) => void;
  onFullscreenRequest?: () => void;
  onGoToStart?: (currentTime: number) => void;
  onGoToEnd?: (endTime: number, startTime: number, duration: number) => void;
  className?: string;
  previewStyle?: React.CSSProperties;
  enableMarkers?: boolean;
  showNoMovieIcon?: boolean;
  existingMarkers?: Array<{
    start: number;
    end: number;
    id?: number;
  }>;
  initialMarker?: {
    start: number;
    end: number;
  } | null;
  overlayContent?: React.ReactNode;
}

interface HoverPosition {
  pos: number;
  time: number;
  x: number;
}

interface Marker {
  start: number;
  end: number;
  duration: number;
  id: number;
}

const VideoPlayer: React.FC<VideoPlayerProps> = ({
  url,
  poster,
  onReady,
  onStart,
  onError,
  onProgress,
  onEnd,
  onPause,
  onSeek,
  onFullscreenRequest,
  onGoToStart,
  onGoToEnd,
  className = '',
  previewStyle,
  enableMarkers = true,
  showNoMovieIcon = false,
  existingMarkers = [],
  initialMarker = null,
  overlayContent
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const controlsTimeout = useRef<NodeJS.Timeout | null>(null);
  const progressBarRef = useRef<HTMLDivElement>(null);
  const qrVisualizationBarRef = useRef<HTMLDivElement>(null);

  // ✅ AJOUT v3.2.0 : Ref pour éviter les boucles de synchronisation
  const isInternalUpdate = useRef(false);
  const previousInitialMarker = useRef<typeof initialMarker>(null);

  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [showControls, setShowControls] = useState(true);
  const [showSettings, setShowSettings] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [controlsBottom, setControlsBottom] = useState(0);
  const [showCursor, setShowCursor] = useState(true);

  const [hoverPosition, setHoverPosition] = useState<HoverPosition | null>(null);
  const [markMode, setMarkMode] = useState<'start' | 'end'>(
    initialMarker ? 'end' : 'start'
  );
  const [startTimecode, setStartTimecode] = useState<number | null>(
    initialMarker ? initialMarker.start : null
  );
  const [markers, setMarkers] = useState<Marker[]>(() => {
    if (initialMarker) {
      return [{
        start: initialMarker.start,
        end: initialMarker.end,
        duration: initialMarker.end - initialMarker.start,
        id: Date.now()
      }];
    }
    return [];
  });
  const [showMarkingButtons, setShowMarkingButtons] = useState(false);
  const [activeButton, setActiveButton] = useState<'start' | 'end' | null>(null);
  const [editingMarkerId, setEditingMarkerId] = useState<number | null>(null);

  // ✅ AJOUT v3.2.0 : Synchronisation avec initialMarker externe
  useEffect(() => {
    // Ignorer si la mise à jour vient de nous-mêmes
    if (isInternalUpdate.current) {
      console.log('🔄 VideoPlayer: Ignoring internal update');
      isInternalUpdate.current = false;
      return;
    }

    // Vérifier si initialMarker a vraiment changé
    const hasChanged = 
      previousInitialMarker.current?.start !== initialMarker?.start ||
      previousInitialMarker.current?.end !== initialMarker?.end;

    if (!hasChanged) {
      console.log('🔄 VideoPlayer: No change detected', {
        prev: previousInitialMarker.current,
        current: initialMarker
      });
      return;
    }

    console.log('🔄 VideoPlayer: initialMarker changed from parent', {
      previous: previousInitialMarker.current,
      new: initialMarker
    });

    if (initialMarker) {
      // Mettre à jour le marker existant
      const newMarker = {
        start: initialMarker.start,
        end: initialMarker.end,
        duration: initialMarker.end - initialMarker.start,
        id: markers[0]?.id || Date.now()
      };
      
      console.log('🔄 VideoPlayer: Setting new marker', newMarker);
      
      setMarkers([newMarker]);
      setStartTimecode(initialMarker.start);
      setMarkMode('end');
      
      // ✅ AJOUT v3.2.2 : Activer le mode édition pour afficher les boutons
      setEditingMarkerId(newMarker.id);
      setShowMarkingButtons(true);
      
      // Déplacer le curseur vidéo pour refléter le changement (seulement si on change le start)
      if (videoRef.current && previousInitialMarker.current?.start !== initialMarker.start) {
        videoRef.current.currentTime = initialMarker.start;
      }
    } else {
      // Réinitialiser si initialMarker devient null
      console.log('🔄 VideoPlayer: Resetting markers');
      setMarkers([]);
      setStartTimecode(null);
      setMarkMode('start');
      setEditingMarkerId(null);
      setShowMarkingButtons(false);
    }

    previousInitialMarker.current = initialMarker;
  }, [initialMarker?.start, initialMarker?.end]);

  useEffect(() => {
    const handleFullscreenChange = () => {
      const isNowFullscreen = !!(
        document.fullscreenElement ||
        (document as any).webkitFullscreenElement ||
        (document as any).mozFullScreenElement ||
        (document as any).msFullscreenElement
      );
      
      setIsFullscreen(isNowFullscreen);

      if (isNowFullscreen && containerRef.current) {
        const screenHeight = window.innerHeight;
        const controlsHeight = 100;
        const bottomValue = -screenHeight + controlsHeight;
        setControlsBottom(bottomValue);
      } else {
        setControlsBottom(0);
      }
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('msfullscreenchange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
      document.removeEventListener('msfullscreenchange', handleFullscreenChange);
    };
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !url) return;

    if (Hls.isSupported()) {
      const hls = new Hls();

      hls.on(Hls.Events.MEDIA_ATTACHED, () => {
        if (onReady) onReady();
        hls.loadSource(url);
      });

      hls.on(Hls.Events.ERROR, (event, data) => {
        if (data.fatal) {
          hls.destroy();
        }
        if (onError) onError(data);
      });

      hls.attachMedia(video);

      return () => {
        hls.destroy();
      };
    } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
      video.src = url;
      if (onReady) onReady();
    }
  }, [url, onReady, onError]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleTimeUpdate = () => {
      setCurrentTime(video.currentTime);
      if (onProgress) onProgress(video.currentTime);
    };

    const handleLoadedMetadata = () => {
      setDuration(video.duration);
    };

    const handleEnded = () => {
      setIsPlaying(false);
      if (onEnd) onEnd();
    };

    const handlePlay = () => {
      setIsPlaying(true);
      if (onStart) onStart();
    };

    const handlePause = () => {
      setIsPlaying(false);
      if (onPause) onPause();
    };

    const handleSeeked = () => {
      if (onSeek) onSeek(video.currentTime);
    };

    video.addEventListener('timeupdate', handleTimeUpdate);
    video.addEventListener('loadedmetadata', handleLoadedMetadata);
    video.addEventListener('ended', handleEnded);
    video.addEventListener('play', handlePlay);
    video.addEventListener('pause', handlePause);
    video.addEventListener('seeked', handleSeeked);

    return () => {
      video.removeEventListener('timeupdate', handleTimeUpdate);
      video.removeEventListener('loadedmetadata', handleLoadedMetadata);
      video.removeEventListener('ended', handleEnded);
      video.removeEventListener('play', handlePlay);
      video.removeEventListener('pause', handlePause);
      video.removeEventListener('seeked', handleSeeked);
    };
  }, [onProgress, onEnd, onStart, onPause, onSeek]);

  const togglePlay = () => {
    const video = videoRef.current;
    if (!video) return;

    if (isPlaying) {
      video.pause();
    } else {
      video.play();
    }
  };

  const seekVideo = (time: number) => {
    const video = videoRef.current;
    if (!video) return;
    video.currentTime = time;
  };

  const handleSeek = (e: React.MouseEvent<HTMLDivElement>) => {
    const video = videoRef.current;
    if (!video || !progressBarRef.current) return;

    const rect = progressBarRef.current.getBoundingClientRect();
    const pos = (e.clientX - rect.left) / rect.width;
    let time = pos * duration;

    if (!showMarkingButtons) {
      video.currentTime = time;
      return;
    }

    if (enableMarkers && activeButton !== null) {
      if (activeButton === 'start') {
        time = Math.max(0, time);
        
        if (markMode === 'end' && editingMarkerId !== null) {
          const editingMarker = markers.find(m => m.id === editingMarkerId);
          if (editingMarker) {
            time = Math.min(time, editingMarker.end);
          }
        }
        
        setStartTimecode(time);
        setHoverPosition({ pos: time / duration, time, x: (time / duration) * rect.width });
        video.currentTime = time;
      } else if (activeButton === 'end') {
        time = Math.min(time, duration);
        if (startTimecode !== null) {
          time = Math.max(time, startTimecode);
        }
        setHoverPosition({ pos: time / duration, time, x: (time / duration) * rect.width });
        video.currentTime = time;
      }
    }
  };

  const handleProgressHover = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!enableMarkers || !showMarkingButtons || activeButton === null) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const pos = (e.clientX - rect.left) / rect.width;
    let time = pos * duration;
    
    if (activeButton === 'start') {
      time = Math.max(0, time);
      
      if (markMode === 'end' && editingMarkerId !== null) {
        const editingMarker = markers.find(m => m.id === editingMarkerId);
        if (editingMarker) {
          time = Math.min(time, editingMarker.end);
        }
      }
      
      setStartTimecode(time);
      setHoverPosition({ pos: time / duration, time, x: (time / duration) * rect.width });
      seekVideo(time);
    } else if (activeButton === 'end') {
      time = Math.min(time, duration);
      if (startTimecode !== null) {
        time = Math.max(time, startTimecode);
      }
      setHoverPosition({ pos: time / duration, time, x: (time / duration) * rect.width });
      seekVideo(time);
    }
  };

  const handleProgressLeave = () => {
  };

  const handleQrBarClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    
    if (!qrVisualizationBarRef.current || !progressBarRef.current) return;

    const rect = qrVisualizationBarRef.current.getBoundingClientRect();
    const pos = (e.clientX - rect.left) / rect.width;
    const time = pos * duration;

    const clickedMarker = markers.find(marker => 
      time >= marker.start && time <= marker.end
    );

    if (clickedMarker) {
      if (showMarkingButtons && editingMarkerId === clickedMarker.id) {
        setShowMarkingButtons(false);
        setHoverPosition(null);
        setStartTimecode(null);
        setActiveButton(null);
        setEditingMarkerId(null);
        setMarkMode('start');
        return;
      }
      
      setEditingMarkerId(clickedMarker.id);
      setStartTimecode(clickedMarker.start);
      setMarkMode('end');
      setHoverPosition(null);
      setShowMarkingButtons(true);
      setActiveButton(null);
    } else {
      setShowMarkingButtons(true);
      setMarkMode('start');
      setActiveButton('start');
      setEditingMarkerId(null);
      
      const progressRect = progressBarRef.current.getBoundingClientRect();
      const clickTime = time;
      setHoverPosition({ 
        pos: clickTime / duration, 
        time: clickTime, 
        x: (clickTime / duration) * progressRect.width
      });
      setStartTimecode(clickTime);
      seekVideo(clickTime);
    }
  };

  const markTimecode = () => {
    if (!hoverPosition) return;

    if (markMode === 'start' && activeButton === 'start') {
      setStartTimecode(hoverPosition.time);
      setMarkMode('end');
      setActiveButton('end');
      
      // ✅ MODIFICATION v3.2.0 : Marquer comme mise à jour interne
      isInternalUpdate.current = true;
      if (onGoToStart) onGoToStart(hoverPosition.time);
    } else if (markMode === 'end' && activeButton === 'end') {
      if (hoverPosition.time <= startTimecode!) {
        return;
      }

      const start = startTimecode!;
      const end = hoverPosition.time;
      const markerDuration = end - start;

      if (editingMarkerId !== null) {
        setMarkers(markers.map(m => 
          m.id === editingMarkerId 
            ? { ...m, start, end, duration: markerDuration }
            : m
        ));
        setEditingMarkerId(null);
        
        setMarkMode('start');
        setActiveButton(null);
        setHoverPosition(null);
      } else {
        const newMarker: Marker = {
          start,
          end,
          duration: markerDuration,
          id: Date.now()
        };
        setMarkers([...markers, newMarker]);
        
        setMarkMode('start');
        setActiveButton(null);
        setHoverPosition(null);
        setShowMarkingButtons(false);
        setStartTimecode(null);
      }

      // ✅ MODIFICATION v3.2.0 : Marquer comme mise à jour interne
      isInternalUpdate.current = true;
      if (onGoToEnd) onGoToEnd(end, start, markerDuration);
    }
  };

  const cancelMarking = () => {
    if (editingMarkerId !== null) {
      setMarkers(markers.filter(m => m.id !== editingMarkerId));
    }
    
    setMarkMode('start');
    setStartTimecode(null);
    setHoverPosition(null);
    setShowMarkingButtons(false);
    setActiveButton(null);
    setEditingMarkerId(null);
  };

  const toggleMute = () => {
    const video = videoRef.current;
    if (!video) return;

    video.muted = !isMuted;
    setIsMuted(!isMuted);
  };

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const video = videoRef.current;
    if (!video) return;

    const newVolume = parseFloat(e.target.value);
    video.volume = newVolume;
    setVolume(newVolume);
    setIsMuted(newVolume === 0);
  };

  const toggleFullscreen = () => {
    if (containerRef.current) {
      if (document.fullscreenElement) {
        document.exitFullscreen();
      } else {
        containerRef.current.requestFullscreen();
      }
			
      if (onFullscreenRequest) {
        onFullscreenRequest();
      }			
    }
  };

  const skipTime = (seconds: number) => {
    const video = videoRef.current;
    if (!video) return;
    video.currentTime = Math.max(0, Math.min(video.currentTime + seconds, duration));
  };

  const changePlaybackRate = (rate: number) => {
    const video = videoRef.current;
    if (!video) return;
    video.playbackRate = rate;
    setPlaybackRate(rate);
    setShowSettings(false);
  };

  const formatTime = (time: number) => {
    if (isNaN(time)) return '0:00';
    const mins = Math.floor(time / 60);
    const secs = Math.floor(time % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleMouseMove = () => {
    setShowControls(true);
    setShowCursor(true);
    
    if (controlsTimeout.current) {
      clearTimeout(controlsTimeout.current);
    }
    
    controlsTimeout.current = setTimeout(() => {
      setShowControls(false);
      setShowCursor(false);
    }, 5000);
  };

  useEffect(() => {
    return () => {
      if (controlsTimeout.current) {
        clearTimeout(controlsTimeout.current);
      }
    };
  }, []);

  if (!url && showNoMovieIcon) {
    return (
      <div
        className={`${styles.videoPlayerContainer} ${styles.noMovie} ${className}`}
        style={previewStyle}
      >
        <div className={styles.noMovieIcon}>
          <Film size={80} strokeWidth={1.5} />
          <p>Aucun film chargé</p>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className={`${styles.videoPlayerContainer} ${isFullscreen ? styles.fullscreen : ''} ${className}`}
      style={{
        ...previewStyle,
        cursor: showCursor ? 'default' : 'none'
      }}
      onMouseMove={handleMouseMove}
    >
      <video
        ref={videoRef}
        poster={poster}
        className={styles.video}
        onClick={togglePlay}
      />

      {overlayContent && (
        <div className={styles.overlayContent}>
          {overlayContent}
        </div>
      )}

      <div 
        className={`${styles.controls} ${showControls ? styles.controlsVisible : styles.controlsHidden}`}
        style={isFullscreen ? { bottom: `${controlsBottom}px` } : {}}
      >
        {enableMarkers && (
          <div 
            ref={qrVisualizationBarRef}
            className={styles.qrVisualizationBar}
            style={{ position: 'relative', cursor: 'pointer' }}
            onClick={handleQrBarClick}
          >
            <div 
              className={styles.qrVisualizationBarBackground}
              style={{ pointerEvents: 'none' }}
            />
            
            {markers.map(marker => {
              const leftPercent = (marker.start / duration) * 100;
              const widthPercent = ((marker.end - marker.start) / duration) * 100;
              
              console.log('🎨 VideoPlayer: Rendering marker', {
                marker,
                duration,
                leftPercent,
                widthPercent
              });
              
              return (
                <div
                  key={marker.id}
                  className={styles.qrMarkerBlock}
                  style={{
                    left: `${leftPercent}%`,
                    width: `${widthPercent}%`,
                    opacity: editingMarkerId === marker.id ? 0.5 : 1
                  }}
                />
              );
            })}

            {/* Barres jaunes en mode édition */}
            {startTimecode !== null && markMode === 'end' && hoverPosition && editingMarkerId !== null && (
              <>
                {(() => {
                  const editingMarker = markers.find(m => m.id === editingMarkerId);
                  if (!editingMarker) return null;
                  
                  const originalStart = editingMarker.start;
                  const originalEnd = editingMarker.end;
                  const newStart = activeButton === 'start' ? hoverPosition.time : startTimecode;
                  const newEnd = activeButton === 'end' ? hoverPosition.time : originalEnd;
                  
                  if (activeButton === 'start') {
                    if (newStart < originalStart) {
                      return (
                        <>
                          <div
                            className={styles.qrMarkerBlockTemp}
                            style={{
                              left: `${(newStart / duration) * 100}%`,
                              width: `${((originalStart - newStart) / duration) * 100}%`,
                              pointerEvents: 'none'
                            }}
                          />
                          <div
                            className={styles.qrMarkerBlock}
                            style={{
                              left: `${(originalStart / duration) * 100}%`,
                              width: `${((originalEnd - originalStart) / duration) * 100}%`,
                              pointerEvents: 'none'
                            }}
                          />
                        </>
                      );
                    } else {
                      return (
                        <>
                          <div
                            className={styles.qrMarkerBlockTemp}
                            style={{
                              left: `${(originalStart / duration) * 100}%`,
                              width: `${((newStart - originalStart) / duration) * 100}%`,
                              pointerEvents: 'none'
                            }}
                          />
                          <div
                            className={styles.qrMarkerBlock}
                            style={{
                              left: `${(newStart / duration) * 100}%`,
                              width: `${((originalEnd - newStart) / duration) * 100}%`,
                              pointerEvents: 'none'
                            }}
                          />
                        </>
                      );
                    }
                  }
                  
                  if (activeButton === 'end') {
                    if (newEnd > originalEnd) {
                      return (
                        <>
                          <div
                            className={styles.qrMarkerBlock}
                            style={{
                              left: `${(originalStart / duration) * 100}%`,
                              width: `${((originalEnd - originalStart) / duration) * 100}%`,
                              pointerEvents: 'none'
                            }}
                          />
                          <div
                            className={styles.qrMarkerBlockTemp}
                            style={{
                              left: `${(originalEnd / duration) * 100}%`,
                              width: `${((newEnd - originalEnd) / duration) * 100}%`,
                              pointerEvents: 'none'
                            }}
                          />
                        </>
                      );
                    } else {
                      return (
                        <>
                          <div
                            className={styles.qrMarkerBlock}
                            style={{
                              left: `${(originalStart / duration) * 100}%`,
                              width: `${((newEnd - originalStart) / duration) * 100}%`,
                              pointerEvents: 'none'
                            }}
                          />
                          <div
                            className={styles.qrMarkerBlockTemp}
                            style={{
                              left: `${(newEnd / duration) * 100}%`,
                              width: `${((originalEnd - newEnd) / duration) * 100}%`,
                              pointerEvents: 'none'
                            }}
                          />
                        </>
                      );
                    }
                  }
                  
                  return null;
                })()}
              </>
            )}

            {/* Barre jaune en mode création (simple) */}
            {startTimecode !== null && markMode === 'end' && hoverPosition && editingMarkerId === null && (
              <div
                className={styles.qrMarkerBlockTemp}
                style={{
                  left: `${(Math.min(startTimecode, hoverPosition.time) / duration) * 100}%`,
                  width: `${(Math.abs(hoverPosition.time - startTimecode) / duration) * 100}%`,
                  pointerEvents: 'none'
                }}
              />
            )}

            {/* MODE EDITION : Boutons fixes DEBUT et FIN (toujours visibles en mode édition) */}
            {startTimecode !== null && markMode === 'end' && showMarkingButtons && editingMarkerId !== null && (
              <>
                {/* Bouton DEBUT fixe - visible sauf quand il est actif */}
                {activeButton !== 'start' && (
                  <div
                    className={styles.markButtonFixed}
                    style={{ 
                      left: `${(startTimecode / duration) * 100}%`,
                      zIndex: 1002,
                      pointerEvents: 'none'
                    }}
                  >
                    <div className={styles.triangle}></div>
                    <div className={styles.bubble} style={{ pointerEvents: 'auto' }}>
                      <div className={styles.buttonRow}>
                        <button 
                          className={`${styles.markStart}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            setActiveButton('start');
                            if (progressBarRef.current) {
                              setHoverPosition({ 
                                pos: startTimecode / duration, 
                                time: startTimecode, 
                                x: (startTimecode / duration) * progressBarRef.current.getBoundingClientRect().width
                              });
                            }
                          }}
                        >
                          Début
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            cancelMarking();
                          }}
                          className={styles.cancelButton}
                          title="Annuler"
                        >
                          <X size={14} />
                        </button>
                      </div>
                      <div className={styles.infoRow}>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            const newTime = Math.max(0, startTimecode - 1);
                            if (editingMarkerId !== null) {
                              const editingMarker = markers.find(m => m.id === editingMarkerId);
                              if (editingMarker) {
                                const newDuration = editingMarker.end - newTime;
                                setMarkers(markers.map(m => 
                                  m.id === editingMarkerId 
                                    ? { ...m, start: newTime, duration: newDuration }
                                    : m
                                ));
                                // ✅ MODIFICATION v3.2.0 : Marquer comme mise à jour interne
                                isInternalUpdate.current = true;
                                if (onGoToEnd) onGoToEnd(editingMarker.end, newTime, newDuration);
                              }
                            }
                            setStartTimecode(newTime);
                            seekVideo(newTime);
                            // ✅ MODIFICATION v3.2.0 : Marquer comme mise à jour interne
                            isInternalUpdate.current = true;
                            if (onGoToStart) onGoToStart(newTime);
                          }}
                          className={styles.timeAdjustButton}
                          title="Reculer de 1s"
                        >
                          &lt;
                        </button>
                        <div className={styles.markTime}>
                          {formatTime(startTimecode)}
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            let newTime = startTimecode + 1;
                            if (editingMarkerId !== null) {
                              const editingMarker = markers.find(m => m.id === editingMarkerId);
                              if (editingMarker) {
                                newTime = Math.min(newTime, editingMarker.end - 0.1);
                                const newDuration = editingMarker.end - newTime;
                                setMarkers(markers.map(m => 
                                  m.id === editingMarkerId 
                                    ? { ...m, start: newTime, duration: newDuration }
                                    : m
                                ));
                                // ✅ MODIFICATION v3.2.0 : Marquer comme mise à jour interne
                                isInternalUpdate.current = true;
                                if (onGoToEnd) onGoToEnd(editingMarker.end, newTime, newDuration);
                              }
                            }
                            setStartTimecode(newTime);
                            seekVideo(newTime);
                            // ✅ MODIFICATION v3.2.0 : Marquer comme mise à jour interne
                            isInternalUpdate.current = true;
                            if (onGoToStart) onGoToStart(newTime);
                          }}
                          className={styles.timeAdjustButton}
                          title="Avancer de 1s"
                        >
                          &gt;
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Bouton FIN fixe (seulement en mode édition) - visible sauf quand il est actif */}
                {activeButton !== 'end' && (
                  <div
                    className={styles.markButtonFixed}
                    style={{ 
                      left: `${((markers.find(m => m.id === editingMarkerId)?.end || 0) / duration) * 100}%`,
                      zIndex: 1002,
                      pointerEvents: 'none'
                    }}
                  >
                    <div className={styles.triangle}></div>
                    <div className={styles.bubble} style={{ pointerEvents: 'auto' }}>
                      <div className={styles.buttonRow}>
                        <button 
                          className={`${styles.markEnd}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            setActiveButton('end');
                            const currentEnd = markers.find(m => m.id === editingMarkerId)?.end || 0;
                            if (progressBarRef.current) {
                              setHoverPosition({ 
                                pos: currentEnd / duration, 
                                time: currentEnd, 
                                x: (currentEnd / duration) * progressBarRef.current.getBoundingClientRect().width
                              });
                            }
                          }}
                        >
                          Fin
                        </button>
                      </div>
                      <div className={styles.infoRow}>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            const editingMarker = markers.find(m => m.id === editingMarkerId);
                            if (editingMarker) {
                              const newTime = Math.max(startTimecode + 0.1, editingMarker.end - 1);
                              setMarkers(markers.map(m => 
                                m.id === editingMarkerId 
                                  ? { ...m, end: newTime, duration: newTime - editingMarker.start }
                                  : m
                              ));
                              seekVideo(newTime);
                              // ✅ MODIFICATION v3.2.0 : Marquer comme mise à jour interne
                              isInternalUpdate.current = true;
                              if (onGoToEnd) onGoToEnd(newTime, editingMarker.start, newTime - editingMarker.start);
                            }
                          }}
                          className={styles.timeAdjustButton}
                          title="Reculer de 1s"
                        >
                          &lt;
                        </button>
                        <div className={styles.markTime}>
                          {formatTime(markers.find(m => m.id === editingMarkerId)?.end || 0)}
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            const editingMarker = markers.find(m => m.id === editingMarkerId);
                            if (editingMarker) {
                              const newTime = Math.min(duration, editingMarker.end + 1);
                              setMarkers(markers.map(m => 
                                m.id === editingMarkerId 
                                  ? { ...m, end: newTime, duration: newTime - editingMarker.start }
                                  : m
                              ));
                              seekVideo(newTime);
                              // ✅ MODIFICATION v3.2.0 : Marquer comme mise à jour interne
                              isInternalUpdate.current = true;
                              if (onGoToEnd) onGoToEnd(newTime, editingMarker.start, newTime - editingMarker.start);
                            }
                          }}
                          className={styles.timeAdjustButton}
                          title="Avancer de 1s"
                        >
                          &gt;
                        </button>
                        <div className={styles.separator}>•</div>
                        <div className={styles.markDuration}>
                          {formatTime(Math.abs((markers.find(m => m.id === editingMarkerId)?.end || 0) - startTimecode))}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}

            {/* Boutons mobiles (quand un bouton est actif) */}
            {showMarkingButtons && hoverPosition && activeButton !== null && (
              <div
                className={styles.markButton}
                style={{ 
                  left: `${(hoverPosition.time / duration) * 100}%`,
                  zIndex: 1004,
                  pointerEvents: 'none'
                }}
              >
                <div className={styles.triangle}></div>
                <div className={styles.bubble} style={{ pointerEvents: 'auto' }}>
                  <div className={styles.buttonRow}>
                    {activeButton === 'start' ? (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (editingMarkerId !== null) {
                            const editingMarker = markers.find(m => m.id === editingMarkerId);
                            if (editingMarker && hoverPosition) {
                              const newStart = hoverPosition.time;
                              const newDuration = editingMarker.end - newStart;
                              setMarkers(markers.map(m => 
                                m.id === editingMarkerId 
                                  ? { ...m, start: newStart, duration: newDuration }
                                  : m
                              ));
                              setStartTimecode(newStart);
                              // ✅ MODIFICATION v3.2.0 : Marquer comme mise à jour interne
                              isInternalUpdate.current = true;
                              if (onGoToStart) onGoToStart(newStart);
                              if (onGoToEnd) onGoToEnd(editingMarker.end, newStart, newDuration);
                            }
                            setActiveButton(null);
                            setHoverPosition(null);
                          } else {
                            markTimecode();
                          }
                        }}
                        className={`${styles.markStart} ${styles.activeButton}`}
                      >
                        Début
                      </button>
                    ) : (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (editingMarkerId !== null) {
                            const editingMarker = markers.find(m => m.id === editingMarkerId);
                            if (editingMarker && hoverPosition) {
                              const newEnd = hoverPosition.time;
                              const newDuration = newEnd - editingMarker.start;
                              setMarkers(markers.map(m => 
                                m.id === editingMarkerId 
                                  ? { ...m, end: newEnd, duration: newDuration }
                                  : m
                              ));
                              // ✅ MODIFICATION v3.2.0 : Marquer comme mise à jour interne
                              isInternalUpdate.current = true;
                              if (onGoToEnd) onGoToEnd(newEnd, editingMarker.start, newDuration);
                            }
                            setActiveButton(null);
                            setHoverPosition(null);
                          } else {
                            markTimecode();
                          }
                        }}
                        className={`${styles.markEnd} ${styles.activeButton}`}
                      >
                        Fin
                      </button>
                    )}
                  </div>
                  <div className={styles.infoRow}>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (hoverPosition) {
                          const newTime = Math.max(0, hoverPosition.time - 1);
                          if (activeButton === 'start') {
                            setStartTimecode(newTime);
                            if (progressBarRef.current) {
                              setHoverPosition({ 
                                pos: newTime / duration, 
                                time: newTime, 
                                x: (newTime / duration) * progressBarRef.current.getBoundingClientRect().width
                              });
                            }
                            seekVideo(newTime);
                          } else if (activeButton === 'end' && startTimecode !== null) {
                            const adjustedTime = Math.max(startTimecode + 0.1, newTime);
                            if (progressBarRef.current) {
                              setHoverPosition({ 
                                pos: adjustedTime / duration, 
                                time: adjustedTime, 
                                x: (adjustedTime / duration) * progressBarRef.current.getBoundingClientRect().width
                              });
                            }
                            seekVideo(adjustedTime);
                          }
                        }
                      }}
                      className={styles.timeAdjustButton}
                      title="Reculer de 1s"
                    >
                      &lt;
                    </button>
                    <div className={styles.markTime}>
                      {formatTime(hoverPosition.time)}
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (hoverPosition) {
                          let newTime = hoverPosition.time + 1;
                          if (activeButton === 'start' && editingMarkerId !== null) {
                            const editingMarker = markers.find(m => m.id === editingMarkerId);
                            if (editingMarker) {
                              newTime = Math.min(newTime, editingMarker.end - 0.1);
                            }
                            setStartTimecode(newTime);
                          } else if (activeButton === 'end') {
                            newTime = Math.min(newTime, duration);
                          }
                          if (progressBarRef.current) {
                            setHoverPosition({ 
                              pos: newTime / duration, 
                              time: newTime, 
                              x: (newTime / duration) * progressBarRef.current.getBoundingClientRect().width
                            });
                          }
                          seekVideo(newTime);
                        }
                      }}
                      className={styles.timeAdjustButton}
                      title="Avancer de 1s"
                    >
                      &gt;
                    </button>
                    {activeButton === 'end' && startTimecode !== null && (
                      <>
                        <div className={styles.separator}>•</div>
                        <div className={styles.markDuration}>
                          {formatTime(Math.abs(hoverPosition.time - startTimecode))}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {enableMarkers && existingMarkers && existingMarkers.length > 0 && (
          <div className={styles.existingQrBar}>
            {existingMarkers.map((marker, index) => (
              <div
                key={marker.id ?? index}
                className={styles.existingQrMarkerBlock}
                style={{
                  left: `${(marker.start / duration) * 100}%`,
                  width: `${((marker.end - marker.start) / duration) * 100}%`
                }}
                title={`QR code existant: ${formatTime(marker.start)} - ${formatTime(marker.end)}`}
              />
            ))}
          </div>
        )}

        <div className={styles.progressBarContainer}>
          <div
            ref={progressBarRef}
            className={styles.progressBar}
            onClick={handleSeek}
            onMouseMove={handleProgressHover}
            onMouseDown={handleSeek}
            onMouseLeave={handleProgressLeave}
          >
            <div
              className={styles.progressFill}
              style={{ width: `${(currentTime / duration) * 100}%` }}
            >
              <div className={styles.progressThumb} />
            </div>
          </div>
        </div>

        <div className={styles.controlsRow}>
          <button onClick={togglePlay} className={styles.controlButton}>
            {isPlaying ? <Pause size={24} /> : <Play size={24} />}
          </button>

          <button onClick={() => skipTime(-10)} className={styles.controlButton}>
            <SkipBack size={20} />
          </button>

          <button onClick={() => skipTime(10)} className={styles.controlButton}>
            <SkipForward size={20} />
          </button>

          <span className={styles.timeDisplay}>
            {formatTime(currentTime)} / {formatTime(duration)}
          </span>

          <div className={styles.spacer} />

          <div className={styles.volumeControl}>
            <button onClick={toggleMute} className={styles.controlButton}>
              {isMuted || volume === 0 ? <VolumeX size={20} /> : <Volume2 size={20} />}
            </button>
            <input
              type="range"
              min="0"
              max="1"
              step="0.1"
              value={isMuted ? 0 : volume}
              onChange={handleVolumeChange}
              className={styles.volumeSlider}
            />
          </div>

          <div className={styles.settingsControl}>
            <button
              onClick={() => setShowSettings(!showSettings)}
              className={styles.controlButton}
            >
              <Settings size={20} />
            </button>
            {showSettings && (
              <div className={styles.settingsMenu}>
                <div className={styles.settingsTitle}>Vitesse</div>
                {[0.25, 0.5, 0.75, 1, 1.25, 1.5, 2].map(rate => (
                  <button
                    key={rate}
                    onClick={() => changePlaybackRate(rate)}
                    className={playbackRate === rate ? styles.settingsItemActive : styles.settingsItem}
                  >
                    {rate}x
                  </button>
                ))}
              </div>
            )}
          </div>

          <button onClick={toggleFullscreen} className={styles.controlButton}>
            <Maximize size={20} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default VideoPlayer;